/* Shared scroll engine for the RCF advertorial sections.
   One rAF-throttled listener drives the progress bar and the sticky buy bar.
   Continuous values are written as CSS custom properties so the visual rules
   stay in CSS. */

(() => {
  const SCROLL_ROOT = document.documentElement;
  const REVEAL_THRESHOLD = 0.14;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp01 = (value) => Math.max(0, Math.min(1, value));

  let progressBars = [];
  let stickyBars = [];
  let frameQueued = false;
  let revealObserver = null;

  const updateProgress = () => {
    const max = SCROLL_ROOT.scrollHeight - SCROLL_ROOT.clientHeight;
    const ratio = max > 0 ? clamp01(SCROLL_ROOT.scrollTop / max) : 0;

    for (const bar of progressBars) {
      bar.style.setProperty('--rcfa-progress', `${ratio * 100}%`);
    }
  };

  const updateStickyBars = () => {
    for (const bar of stickyBars) {
      const { rcfaStickyAfter, rcfaStickyOffset } = bar.dataset;
      const trigger = rcfaStickyAfter ? document.querySelector(rcfaStickyAfter) : null;
      // trigger's absolute top: the bar reveals once the preceding section
      // has been fully scrolled past
      const threshold = trigger
        ? trigger.getBoundingClientRect().top + SCROLL_ROOT.scrollTop
        : Number(rcfaStickyOffset) || 600;

      bar.classList.toggle('is-visible', SCROLL_ROOT.scrollTop > threshold);
    }
  };

  const render = () => {
    frameQueued = false;
    updateProgress();
    updateStickyBars();
  };

  const schedule = () => {
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(render);
  };

  const observeReveals = () => {
    const targets = [...document.querySelectorAll('[data-rcfa-reveal]:not(.is-visible)')];
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const target of targets) target.classList.add('is-visible');
      return;
    }

    revealObserver ??= new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }, { threshold: REVEAL_THRESHOLD });

    for (const target of targets) revealObserver.observe(target);
  };


  /* Smooth in-page scrolling for anchor links, offset by the sticky header
     so the target does not land underneath it. */
  const handleScrollLink = (event) => {
    const link = event.target.closest?.('[data-rcfa-scroll-link]');
    if (!link) return;

    const href = link.getAttribute('href') ?? '';
    if (!href.startsWith('#') || href.length < 2) return;

    const target = document.querySelector(href);
    if (!target) return;

    event.preventDefault();

    const { rcfaScrollOffset, rcfaScrollOffsetDesktop } = link.dataset;
    const isDesktop = window.matchMedia('(min-width: 760px)').matches;
    const offset = Number(isDesktop ? rcfaScrollOffsetDesktop : rcfaScrollOffset) || 0;

    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - offset,
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
    });

    // keyboard users should land on the target, not stay on the link
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  const refresh = () => {
    progressBars = [...document.querySelectorAll('[data-rcfa-progress]')];
    stickyBars = [...document.querySelectorAll('[data-rcfa-sticky]')];
    observeReveals();
    schedule();
  };

  document.addEventListener('click', handleScrollLink);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
